import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { mapTransactionStatus, transactionStatusColor } from "utils/transactionStatus";

const formatAmount = (tx) => {
  if (tx.amount) return `${tx.amount} ${tx.currency ?? ""}`.trim();
  const cents = parseInt(tx.amountInCents, 10);
  if (!Number.isNaN(cents)) return `${(cents / 100).toFixed(2)} ${tx.currency ?? ""}`.trim();
  return "—";
};

const columns = [
  { Header: "Date", accessor: "createdTime", align: "left" },
  { Header: "Reference", accessor: (row) => row.referenceId ?? "—", align: "left" },
  { Header: "Amount", accessor: (row) => formatAmount(row), align: "left" },
  {
    Header: "Status",
    accessor: "status",
    align: "left",
    // eslint-disable-next-line react/prop-types
    Cell: ({ value }) => (
      <MDTypography
        variant="caption"
        fontWeight="medium"
        color={transactionStatusColor(value)}
        title={value}
      >
        {mapTransactionStatus(value)}
      </MDTypography>
    ),
  },
  { Header: "Method", accessor: (row) => row.paymentMethodType ?? "—", align: "left" },
  { Header: "Processor", accessor: (row) => row.processorType ?? "—", align: "left" },
  { Header: "Payment ID", accessor: (row) => row.paymentId ?? "—", align: "left" },
];

function SummaryItem({ label, value }) {
  return (
    <MDBox>
      <MDTypography variant="caption" color="text" display="block">
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="medium">
        {value ?? "—"}
      </MDTypography>
    </MDBox>
  );
}

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

SummaryItem.defaultProps = {
  value: "—",
};

export default function PaymentLinkDetail() {
  const { paymentLinkId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`payments-link/paymentDetails/${paymentLinkId}`)
      .then((response) => {
        setDetails(response.data?.data ?? null);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        }
        setDetails(null);
      })
      .finally(() => setLoading(false));
  }, [paymentLinkId]);

  const transactions = details?.transactionDetailsResponses ?? [];
  const sessionAmount = details?.sessionInfo?.amount;
  const amountDisplay = sessionAmount?.amountInCents
    ? `${(parseInt(sessionAmount.amountInCents, 10) / 100).toFixed(2)} ${sessionAmount.currency ?? ""}`
    : "—";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <MDBox display="flex" alignItems="center" mb={2}>
                <IconButton size="small" onClick={() => navigate("/payment-link")} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
                <MDTypography variant="h6" fontWeight="medium">
                  Payment Link Details
                </MDTypography>
              </MDBox>

              <Grid container spacing={3} mb={1}>
                <Grid item xs={12} sm={4}>
                  <SummaryItem label="Link ID" value={paymentLinkId} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <SummaryItem label="Status" value={details?.paymentLinkStatus} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <SummaryItem label="Amount" value={amountDisplay} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <SummaryItem label="Quantity" value={details?.quantity} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <SummaryItem label="Description" value={details?.description} />
                </Grid>
              </Grid>

              <Divider />

              <MDTypography variant="button" fontWeight="medium" mb={1} display="block">
                Transactions
              </MDTypography>
              {transactions.length > 0 ? (
                <DataTable
                  table={{ columns, rows: transactions }}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries
                  noEndBorder
                />
              ) : (
                <MDBox py={4} textAlign="center">
                  <MDTypography variant="body2" color="text">
                    {loading ? "Loading…" : "No transactions yet for this payment link."}
                  </MDTypography>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
